import { faArrowRightLong } from '@fortawesome/pro-regular-svg-icons';
import Image from 'next/image';
import Link from 'next/link';
import styled, { css } from 'styled-components';

import BookTypes from '@components/BookTypes';
import Footer from '@components/Footer';
import Navigation from '@components/Navigation';
import Reviews from '@components/Reviews';
import { backgroundColored, brand, fontDark } from '@constants/colors';
import { laptop } from '@constants/media-queries';
import { allProducts, ourStory, productionUrl } from '@constants/routes';
import { addBreadcrumbsJsonLd } from '@constants/seo';
import { contentWidth, textWidth } from '@constants/sizes';
import { space } from '@constants/spaces';
import Button from '@elements/Button';
import CustomHead from '@elements/CustomHead';
import Text from '@elements/Text';
import Title from '@elements/Title';

const StyledMain = styled.main`
  background-color: ${backgroundColored};
  padding: ${space * 4}px 0px;
  ${laptop} {
    padding: ${space * 7}px 0px;
  }
`;

const TextContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: ${space * 4}px;
  padding: ${space * 4}px ${space * 3}px ${space * 8}px;
`;

const Intro = styled.section`
  margin: 0 auto;
  padding: 0 ${space * 3}px;
  max-width: ${contentWidth};
  text-align: center;
`;

const ImageContainer = styled.div`
  flex: 1;
`;

const StyledTitle = styled(Title)`
  margin-bottom: 8px;
  text-align: center;
  color: ${brand};
  ${laptop} {
    margin-bottom: 20px;
  }
`;

const StyledText = styled(Text)`
  color: ${fontDark};
  max-width: ${textWidth};
`;

const breadcrumbs = [
  {
    '@type': 'ListItem',
    position: 1,
    name: 'Ons verhaal',
  },
];

const AboutUs = () => {
  return (
    <>
      <CustomHead
        title="Ons verhaal"
        description="Het leven zit vol grote en kleine momenten, die je samen of alleen beleeft. Het mooiste is als je ze kunt delen. Die momenten samen maken prachtige verhalen. Belmondo foto helpt je bij het bewaren van deze verhalen."
        canonicalUrl={`${productionUrl}${ourStory}`}
      >
        <script
          type="application/ld+json"
          key="about-us-breadcrumbs-jsonld"
          dangerouslySetInnerHTML={addBreadcrumbsJsonLd(breadcrumbs)}
        />
      </CustomHead>
      <Navigation backgroundColor={backgroundColored} />
      <StyledMain>
        <Intro>
          <StyledTitle size="xl">
            Met Belmondo bewaar je samen de mooiste foto&apos;s én verhalen in
            een boek.
          </StyledTitle>
          <ImageContainer>
            <Image
              src="https://ik.imagekit.io/rys2dzldq/belmondo-foto/webshop/belmondo_faces_G4tWF3pGu.jpg?updatedAt=1715950376420&ik-s=4221a1bfc04ff67e2361ee816ec83e50c701b1eb"
              alt=""
              width={478}
              height={473}
              unoptimized={true}
              layout="intrinsic"
            />
          </ImageContainer>
        </Intro>
        <TextContainer>
          <StyledText size="nm">
            <span>
              De Franse acteur Jean-Paul Belmondo is een bijzondere inspiratie
              geweest voor oprichter Thomas Beekman. In een interview las hij
              dat Belmondo de basis van het acteren geleerd had op de
              toneelschool, maar dat de beste leerschool de straat was geweest.
              De verhalen van de &apos;gewone mensen&apos; in de straten. Daar
              draait het in het leven om. En daar draait het bij Belmondofoto
              ook om. Wij helpen mensen op weg hun eigen verhaal te bewaren,
              maar de verhalen komen van de mensen zelf. Dat is de kern.
            </span>
            <br />
            <br />
            <span>
              Mede door zijn eigen geschiedenis is het Thomas&apos; persoonlijke
              drijfveer dat zo veel mogelijk mensen hun verhalen bewaren en
              delen. Voor zichzelf, maar ook voor mensen met wie ze veel delen
              zoals vrienden, familie en collega&apos;s. Dat de naam Belmondo
              ook een soort Italiaans-Franse verbastering is van mooie wereld
              maakt het verhaal helemaal af.
            </span>
          </StyledText>
          <Link href={allProducts} passHref>
            <Button size="big" iconRight={faArrowRightLong}>
              Begin nu
            </Button>
          </Link>
        </TextContainer>
        <BookTypes />
        <Reviews />
      </StyledMain>
      <Footer />
    </>
  );
};

export default AboutUs;
